import { useEffect, useState, useContext } from 'react';

import { ExperienceTrackerContext, WHITEBOARD_EXPERIENCE } from '@confluence/experience-tracker';
import { useSessionData } from '@confluence/session-data';
import { isUnauthorizedError } from '@confluence/error-boundary';
import { isExternalShareRequest } from '@confluence/external-share-utils';
import { containsNoNetworkError } from '@confluence/network';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';

import type { UseSpaceQueryResult } from './graphql/useSpace';
import { WhiteboardTokenService, type DocumentType } from './services/whiteboard-token-service';

export interface UseTokenServiceOptions {
	tenantId: string;
	space: Pick<UseSpaceQueryResult, 'id'>;
	referenceId?: string | null;
	contentId: string;
	shareToken?: string;
	dataSecurityPolicyPageExport: boolean | null;
	refetch?: () => void;
	documentType?: DocumentType;
	onPermissionChange?: (permissions: string[]) => void;
}

export const useTokenService = ({
	tenantId,
	space,
	referenceId,
	contentId,
	shareToken,
	dataSecurityPolicyPageExport,
	refetch,
	documentType = 'whiteboard',
	onPermissionChange,
}: UseTokenServiceOptions) => {
	const { edition } = useSessionData();
	const [tokenService] = useState(() => new WhiteboardTokenService(documentType));
	const isExternal = isExternalShareRequest();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});

	useEffect(() => {
		tokenService.onInvalidToken(() => {
			void refetch?.();
		});

		tokenService.onTokenChange((token) => {
			if (onPermissionChange) {
				const permissions = tokenService.getPermissions(token);
				onPermissionChange(permissions);
			}
		});

		tokenService.onTokenLoadError((error) => {
			if (isUnauthorizedError(error)) {
				void refetch?.();
			} else if (containsNoNetworkError(error)) {
				experienceTracker.abort({
					name: WHITEBOARD_EXPERIENCE,
					reason: 'Network error while fetching token',
				});
			} else {
				experienceTracker.fail({ name: WHITEBOARD_EXPERIENCE, error });
			}
		});
	}, [tokenService, refetch, experienceTracker, onPermissionChange]);

	// Update the current token whenever the space or contentId changes
	useEffect(() => {
		if (!space.id) {
			return;
		}

		tokenService.setContext({
			spaceId: space.id,
			tenantId,
			contentId,
			confluenceEdition: edition || '',
			isExternal,
			shareToken,
			dataSecurityPolicyPageExport,
			isAIEnabledForContent,
			isRovoEnabledForContent,
		});
		if (referenceId) {
			tokenService.updateToken(referenceId).catch(() => {
				// handled by the `onTokenLoadError` function defined in the useEffect above this
			});
		}

		return () => tokenService.resetToken();
	}, [
		tokenService,
		tenantId,
		space.id,
		referenceId,
		contentId,
		edition,
		shareToken,
		isExternal,
		dataSecurityPolicyPageExport,
		isAIEnabledForContent,
		isRovoEnabledForContent,
	]);

	return tokenService;
};
